/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { useState } from 'react'
import {
  ActionButton,
  Button,
  FileTrigger,
  Flex,
  Heading,
  Radio,
  RadioGroup,
  Text,
} from '@adobe/react-spectrum'
import { useAtom } from 'jotai'

import CloseCircleIcon from '@spectrum-icons/workflow/CloseCircle'
import AddIcon from '@spectrum-icons/workflow/Add'
import RemoveIcon from '@spectrum-icons/workflow/Remove'

import { FormItem } from '../components/FormItem'
import { Stepper } from '../components/Stepper'
import { rad2deg, deg2rad } from './ModelPanel'
import {
  DEFAULT_ADDITIONAL_MODEL,
  DefaultViewerConfig,
  viewerAtom,
} from '../config'

function TransformModel({
  additionalModelIndex,
}: {
  additionalModelIndex: 0 | 1
}) {
  const [atom, setAtom] = useAtom(viewerAtom)
  const model = atom.additionalModels[additionalModelIndex]

  const handleModelPosChange = (axis: string, value: number) => {
    const translation = { ...model.translation }
    // @ts-ignore
    translation[axis] = value

    const additionalModels = atom.additionalModels.map((model, index) =>
      index === additionalModelIndex ? { ...model, translation } : model
    )

    setAtom((prev) => ({
      ...prev,
      additionalModels: [...additionalModels],
    }))
  }

  const handleModelRotChange = (axis: string, value: number) => {
    const rotation = { ...model.rotation }
    // @ts-ignore
    rotation[axis] = deg2rad(value)

    const additionalModels = atom.additionalModels.map((model, index) =>
      index === additionalModelIndex ? { ...model, rotation } : model
    )

    setAtom((prev) => ({
      ...prev,
      additionalModels: [...additionalModels],
    }))
  }

  const handleModelScalingChange = (axis: string, value: number) => {
    const scaling = { ...model.scaling }
    // @ts-ignore
    scaling[axis] = value

    const additionalModels = atom.additionalModels.map((model, index) =>
      index === additionalModelIndex ? { ...model, scaling } : model
    )

    setAtom((prev) => ({
      ...prev,
      additionalModels: [...additionalModels],
    }))
  }

  const handleModelScalingChangeUnified = (value: number) => {
    const scaling = { ...model.scaling }
    scaling.x += value
    scaling.y += value
    scaling.z += value

    const additionalModels = atom.additionalModels.map((model, index) =>
      index === additionalModelIndex ? { ...model, scaling } : model
    )

    setAtom((prev) => ({
      ...prev,
      additionalModels: [...additionalModels],
    }))
  }

  return (
    <>
      <Flex gap="size-300" marginTop={'size-200'} direction={'column'}>
        <Flex gap="size-100" direction={'column'} rowGap={'size-10'}>
          <Text>Position</Text>
          <Flex gap="size-100">
            <Stepper
              step={0.01}
              label="X"
              defaultValue={DEFAULT_ADDITIONAL_MODEL.translation.x}
              value={model.translation.x}
              onChange={handleModelPosChange}
            />
            <Stepper
              step={0.01}
              label="Y"
              defaultValue={DEFAULT_ADDITIONAL_MODEL.translation.y}
              value={model.translation.y}
              onChange={handleModelPosChange}
            />
            <Stepper
              step={0.01}
              label="Z"
              defaultValue={DEFAULT_ADDITIONAL_MODEL.translation.z}
              value={model.translation.z}
              onChange={handleModelPosChange}
            />
          </Flex>
        </Flex>

        <Flex gap="size-100" direction={'column'} rowGap={'size-10'}>
          <Text>Rotation</Text>
          <Flex gap="size-100">
            <Stepper
              step={10}
              label="X"
              defaultValue={DEFAULT_ADDITIONAL_MODEL.rotation.x}
              value={rad2deg(model.rotation.x)}
              onChange={handleModelRotChange}
            />

            <Stepper
              step={10}
              label="Y"
              defaultValue={DEFAULT_ADDITIONAL_MODEL.rotation.y}
              value={rad2deg(model.rotation.y)}
              onChange={handleModelRotChange}
            />

            <Stepper
              step={10}
              label="Z"
              defaultValue={DEFAULT_ADDITIONAL_MODEL.rotation.z}
              value={rad2deg(model.rotation.z)}
              onChange={handleModelRotChange}
            />
          </Flex>
        </Flex>

        <Flex gap="size-100" direction={'column'} rowGap={'size-10'}>
          <Flex gap="size-100" alignItems={'center'}>
            <Text>Scaling</Text>
            <ActionButton
              isQuiet
              onPress={() => handleModelScalingChangeUnified(0.1)}
              aria-label="increase scaling"
            >
              <AddIcon />
            </ActionButton>
            <ActionButton
              isQuiet
              onPress={() => handleModelScalingChangeUnified(-0.1)}
              aria-label="decrease scaling"
            >
              <RemoveIcon />
            </ActionButton>
          </Flex>
          <Flex gap="size-100">
            <Flex gap="size-100" direction="column">
              <Stepper
                step={0.1}
                label="X"
                minValue={0.1}
                defaultValue={DEFAULT_ADDITIONAL_MODEL.scaling.x}
                value={model.scaling.x}
                onChange={handleModelScalingChange}
              />
            </Flex>
            <Flex gap="size-100" direction="column">
              <Stepper
                step={0.1}
                label="Y"
                minValue={0.1}
                defaultValue={DEFAULT_ADDITIONAL_MODEL.scaling.y}
                value={model.scaling.y}
                onChange={handleModelScalingChange}
              />
            </Flex>
            <Flex gap="size-100" direction="column">
              <Stepper
                step={0.1}
                label="Z"
                minValue={0.1}
                defaultValue={DEFAULT_ADDITIONAL_MODEL.scaling.z}
                value={model.scaling.z}
                onChange={handleModelScalingChange}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

function ModelSection({
  title,
  additionalModelIndex,
}: {
  title: string
  additionalModelIndex: 0 | 1
}) {
  const [atom, setAtom] = useAtom(viewerAtom)
  let [layerSelected, setLayerSelected] = useState('viewer')
  const model = atom.additionalModels[additionalModelIndex]

  const handleFileUpdated = (e: any) => {
    let file: any = Array.from(e)[0]

    const additionalModels = atom.additionalModels.map((model, index) =>
      index === additionalModelIndex
        ? {
            ...model,
            filename: file.name,
            src: URL.createObjectURL(file),
            layer: layerSelected,
          }
        : model
    )

    setAtom((prev) => ({
      ...prev,
      additionalModels: [...additionalModels],
    }))
  }

  const handleFileRemoved = () => {
    const additionalModels = atom.additionalModels.map((model, index) =>
      index === additionalModelIndex ? DEFAULT_ADDITIONAL_MODEL : model
    )
    setAtom((prev) => ({
      ...prev,
      additionalModels: [...additionalModels],
    }))
  }

  return (
    <>
      <Heading level={2}>{title}</Heading>
      <FormItem>
        <Flex gap="size-100">
          <RadioGroup
            label="Select the model layer"
            value={layerSelected}
            onChange={setLayerSelected}
          >
            <Radio value="viewer">Viewer</Radio>
            <Radio value="node">Node</Radio>
          </RadioGroup>
        </Flex>
      </FormItem>
      <FormItem>
        <Flex gap="size-100" alignItems={'center'}>
          <FileTrigger
            onSelect={handleFileUpdated}
            acceptedFileTypes={[DefaultViewerConfig.additionalModels[0].loader]}
          >
            <Button variant="primary">Select a file</Button>
          </FileTrigger>

          {model.filename && (
            <>
              <Text
                maxWidth={'90px'}
                UNSAFE_style={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {model.filename}
              </Text>
              <ActionButton isQuiet onPress={handleFileRemoved}>
                <CloseCircleIcon />
              </ActionButton>
            </>
          )}
        </Flex>

        {model.filename && (
          <TransformModel additionalModelIndex={additionalModelIndex} />
        )}
      </FormItem>
    </>
  )
}

function MultiModelsPanel() {
  return (
    <>
      <Heading level={1}>Add Models</Heading>
      <ModelSection title="Model One" additionalModelIndex={0} />
      <ModelSection title="Model Two" additionalModelIndex={1} />
    </>
  )
}

export { MultiModelsPanel }
